import { useMemo, useRef } from "react";
import * as React from "react";

const centerX = 115.1;
const centerY = 51.5;
const secondHandLength = 30;
const minuteHandLength = 30;
const hourHandLength = 16;

const setAttributes = (
    handRef: React.MutableRefObject<SVGLineElement | undefined>,
    attributes: {
        y2: number;
        x2: number;
    }
) => {
    if (handRef.current) {
        handRef.current.setAttribute("x2", String(attributes.x2));
        handRef.current.setAttribute("y2", String(attributes.y2));
    }
};

const calculateHands = () => {
    const date = new Date();
    const seconds = date.getSeconds() + (Math.ceil(date.getMilliseconds() / 200) * 200) / 1000;

    const secondHand = {
        x2: centerX + secondHandLength * Math.cos((Math.PI * (seconds - 15)) / 30),
        y2: centerY + secondHandLength * Math.sin((Math.PI * (seconds - 15)) / 30),
    };

    let minutes = date.getMinutes() + seconds / 60;
    const minuteHand = {
        x2: centerX + minuteHandLength * Math.cos((Math.PI * (minutes - 15)) / 30),
        y2: centerY + minuteHandLength * Math.sin((Math.PI * (minutes - 15)) / 30),
    };

    let hours = (date.getHours() % 12) + minutes / 60;
    const hourHand = {
        x2: centerX + hourHandLength * Math.cos((2 * Math.PI * (hours - 3)) / 12),
        y2: centerY + hourHandLength * Math.sin((2 * Math.PI * (hours - 3)) / 12),
    };
    return { secondHand, minuteHand, hourHand };
};

export const TimeLogsClock: React.FC = () => {
    const requestRef = React.useRef<number>();
    const previousTimeRef = React.useRef<number>();

    const secondHandRef = useRef<SVGLineElement>();
    const minuteHandRef = useRef<SVGLineElement>();
    const hourHandRef = useRef<SVGLineElement>();

    const animate = (time: number) => {
        if (previousTimeRef.current !== undefined) {
            const { secondHand, minuteHand, hourHand } = calculateHands();
            setAttributes(secondHandRef, secondHand);
            setAttributes(minuteHandRef, minuteHand);
            setAttributes(hourHandRef, hourHand);
        }
        previousTimeRef.current = time;
        requestRef.current = requestAnimationFrame(animate);
    };

    React.useEffect(() => {
        requestRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(requestRef.current);
    }, []);

    const { secondHand, minuteHand, hourHand } = useMemo(() => calculateHands(), []);

    return (
        <svg
            version="1.0"
            xmlns="http://www.w3.org/2000/svg"
            x="0px"
            y="0px"
            viewBox="0 0 230 110"
            width="400px"
            height="170px"
        >
            <defs>
                <path
                    id="wave"
                    fill="none"
                    stroke="#C7C9D4"
                    strokeLinecap="round"
                    strokeMiterlimit="10"
                    d="M35.9,36.6c-1.1,0.9-1.1,2.6,0,3.5l0,0
	c1.1,0.9,1.1,2.6,0,3.5l0,0c-1.1,0.9-1.1,2.6,0,3.5l0,0c1.1,0.9,1.1,2.6,0,3.5l0,0c-1.1,0.9-1.1,2.6,0,3.5l0,0
	c1.1,0.9,1.1,2.6,0,3.5l0,0c-1.1,0.9-1.1,2.6,0,3.5"
                />
            </defs>
            <use xlinkHref="#wave" x="0" y="40">
                <animate attributeName="y" from="40" to="-40" dur="5s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="1" to="-0.5" dur="5s" repeatCount="indefinite" />
            </use>
            <use xlinkHref="#wave" x="-5" y="40">
                <animate attributeName="y" begin="0.9s" from="40" to="-40" dur="4s" repeatCount="indefinite" />
                <animate attributeName="opacity" begin="0.9s" from="1" to="-0.5" dur="4s" repeatCount="indefinite" />
            </use>
            <use xlinkHref="#wave" x="5" y="40">
                <animate attributeName="y" begin="1.5s" from="40" to="-40" dur="3.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" begin="1.5s" from="1" to="-0.5" dur="3.5s" repeatCount="indefinite" />
            </use>
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="5"
                y1="108"
                x2="225"
                y2="108"
            />
            <path
                fill="#FFFFFF"
                stroke="#C7C9D4"
                strokeMiterlimit="10"
                d="M69.8,107.9h-4.3v-5.5h9.6v0.2C75,105.5,72.7,107.9,69.8,107.9z"
            />
            <path
                fill="#FFFFFF"
                stroke="#C7C9D4"
                strokeMiterlimit="10"
                d="M160.1,107.9h4.3v-5.5h-9.6v0.2
	C154.9,105.5,157.2,107.9,160.1,107.9z"
            />
            <path
                fill="#FFFFFF"
                stroke="#C7C9D4"
                strokeMiterlimit="10"
                d="M164.5,105h-99V51.1C65.5,24,87.5,2,114.7,2h0.6
	c27.1,0,49.1,22,49.1,49.1V105H164.5z"
            />
            <circle fill="none" stroke="#C7C9D4" strokeMiterlimit="10" cx="115" cy="50.9" r="42.2" />
            <path fill="none" stroke="#E3E4EC" strokeLinecap="round" strokeMiterlimit="10" d="M115,54.5" />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="115"
                y1="82.8"
                x2="115"
                y2="89.1"
            />
            <circle fill="#E3E4EC" cx="115" cy="50.9" r="2.4" />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="83.1"
                y1="50.9"
                x2="76.8"
                y2="50.9"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="153.2"
                y1="50.9"
                x2="146.9"
                y2="50.9"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="92.4"
                y1="28.3"
                x2="88"
                y2="23.9"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="142"
                y1="77.9"
                x2="137.6"
                y2="73.5"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="92.4"
                y1="73.5"
                x2="88"
                y2="77.9"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="142"
                y1="23.9"
                x2="137.6"
                y2="28.3"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="102.9"
                y1="83.6"
                x2="101.7"
                y2="86.7"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="82.1"
                y1="38.7"
                x2="79.2"
                y2="37.6"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="150.8"
                y1="64.2"
                x2="147.5"
                y2="62.9"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="100.6"
                y1="19.5"
                x2="99.1"
                y2="16.2"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="130.9"
                y1="85.6"
                x2="129.6"
                y2="82.7"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="83.6"
                y1="65.3"
                x2="80.3"
                y2="66.8"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="149.7"
                y1="35"
                x2="146.9"
                y2="36.3"
            />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="128.3"
                y1="15.1"
                x2="126.9"
                y2="18.8"
            />
            <path
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                d="M88,42.1c1-3.1,2.5-6,4.5-8.5"
            />
            <path
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                d="M86.6,50.9c0-1,0.1-2,0.2-3"
            />
            <path
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                d="M88.7,61.7c-0.5-1.3-1-2.7-1.3-4.1
	c-0.4-1.4-0.6-2.9-0.7-4.4"
            />
            <path
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                d="M134.8,30.6c5.3,5.2,8.5,12.3,8.5,20.3
	c0,15.7-12.7,28.4-28.4,28.4c-9.8,0-18.4-4.9-23.5-12.4"
            />
            <path
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                d="M132.5,33.4c4.5,4.5,7.3,10.7,7.3,17.5
	c0,13.7-11.1,24.8-24.8,24.8S90.2,64.6,90.2,50.9"
            />
            <circle fill="none" stroke="#C7C9D4" strokeMiterlimit="10" cx="115" cy="50.9" r="39.9" />
            <line
                ref={hourHandRef}
                fill="none"
                stroke="#C7C9D4"
                strokeWidth="4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1={centerX}
                y1={centerY}
                {...hourHand}
            />
            <line
                ref={minuteHandRef}
                fill="none"
                stroke="#C7C9D4"
                strokeWidth="4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1={centerX}
                y1={centerY}
                {...minuteHand}
            />
            <line
                ref={secondHandRef}
                fill="none"
                stroke="#C7C9D4"
                strokeWidth="3"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1={centerX}
                y1={centerY}
                {...secondHand}
            />
            <circle fill="#FFFFFF" stroke="#C7C9D4" strokeMiterlimit="10" cx="115" cy="50.9" r="4.3" />
            <circle fill="#C7C9D4" cx="115" cy="50.9" r="2.1" />
            <line
                fill="none"
                stroke="#C7C9D4"
                strokeLinecap="round"
                strokeMiterlimit="10"
                x1="115"
                y1="12.7"
                x2="115"
                y2="19"
            />
            <g>
                <rect
                    x="170.2"
                    y="65"
                    transform="matrix(1.534422e-03 -1 1 1.534422e-03 118.2648 253.3466)"
                    fill="#FFFFFF"
                    stroke="#C7C9D4"
                    strokeLinejoin="round"
                    strokeMiterlimit="10"
                    width="31.7"
                    height="5"
                />
                <polyline
                    fill="none"
                    stroke="#C7C9D4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeMiterlimit="10"
                    points="
		183.5,51.6 186,41.6 188.5,51.6 	"
                />
            </g>
            <g>
                <rect
                    x="181.1"
                    y="70.6"
                    transform="matrix(6.278546e-02 -0.998 0.998 6.278546e-02 111.777 264.9218)"
                    fill="#FFFFFF"
                    stroke="#C7C9D4"
                    strokeLinejoin="round"
                    strokeMiterlimit="10"
                    width="31.7"
                    height="4.7"
                />
                <polyline
                    fill="none"
                    stroke="#C7C9D4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeMiterlimit="10"
                    points="195.6,57
		198.6,47.1 200.3,57.3 	"
                />
            </g>
            <g>
                <rect
                    x="176.9"
                    y="60.4"
                    transform="matrix(7.341789e-02 -0.9973 0.9973 7.341789e-02 116.017 250.3694)"
                    fill="#FFFFFF"
                    stroke="#C7C9D4"
                    strokeLinejoin="round"
                    strokeMiterlimit="10"
                    width="31.7"
                    height="4.7"
                />
                <polyline
                    fill="none"
                    stroke="#C7C9D4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeMiterlimit="10"
                    points="
		191.6,46.8 194.7,36.9 196.3,47.1 	"
                />
            </g>
            <polygon
                fill="#FFFFFF"
                stroke="#C7C9D4"
                strokeMiterlimit="10"
                points="198.9,107.9 181.6,107.8 179.7,68 201,68 "
            />
            <polygon fill="#C7C9D4" points="185,47.2 186.8,46 186,41.6 " />
            <polygon fill="#C7C9D4" points="197.3,52.3 199.4,51.7 198.6,47.2 " />
            <polygon fill="#C7C9D4" points="195.6,42.6 193.3,41.2 194.5,36.9 " />
            <path
                fill="#FFFFFF"
                stroke="#C7C9D4"
                strokeMiterlimit="10"
                d="M27.6,99.1L27.6,99.1c-8.4,0-15.2-6.8-15.2-15.2V75h30.3v8.9
	C42.8,92.3,36,99.1,27.6,99.1z"
            />
            <path
                fill="#FFFFFF"
                stroke="#C7C9D4"
                strokeMiterlimit="10"
                d="M30.9,97.7L30.9,97.7c-8.4,0-15.2-6.8-15.2-15.2v-4.7H46v4.7
	C46,90.9,39.3,97.7,30.9,97.7z"
            />
            <path
                fill="#FFFFFF"
                stroke="#C7C9D4"
                strokeMiterlimit="10"
                d="M35.3,107.6L35.3,107.6c-8.4,0-15.2-6.8-15.2-15.2V75h30.3v17.5
	C50.4,100.8,43.6,107.6,35.3,107.6z"
            />
        </svg>
    );
};
